import { useCountry } from "hooks/useCountry";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { defaultCountries, parseCountry } from "react-international-phone";
import memoize from "utils/memoize";
import { TextField } from "./TextField";
import { validStytchDialCodes } from "@RHCommerceDev/utils/constants";

const supportedCountries = defaultCountries.filter(ct => {
  const country = parseCountry(ct);
  return validStytchDialCodes?.includes(country?.dialCode);
});

const countries = (defaultCountries as any).map(ct => parseCountry(ct));
type FormValues = {
  code: {
    areaCodes: string;
    dialCode: string;
    format: string;
    iso2: string;
    name: string;
    priority: number | undefined;
  };
  phone: string;
  finalPhoneNo: string;
};

interface StytchPhoneTextFieldProps {
  value: string;
  onChange: (value: string) => void;
}

export const StytchPhoneTextField: React.FC<StytchPhoneTextFieldProps> = ({
  value,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [phoneDetails, setPhoneDetails] = useState<FormValues>({
    code: countries[0],
    phone: value,
    finalPhoneNo: value
  });
  const countryCode = useCountry();
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (countryCode) {
      const country = parseCountry(
        defaultCountries.find(
          (ct: any) => parseCountry(ct)?.iso2 === countryCode?.toLowerCase()
        ) as any
      );
      if (country) {
        setPhoneDetails(prev => ({
          ...prev,
          code: {
            areaCodes: "",
            dialCode: country?.dialCode,
            format: "",
            iso2: country?.iso2,
            name: country?.name,
            priority: country?.priority
          }
        }));
      }
    }
  }, [countryCode]);

  const handleCountryChange = option => {
    setPhoneDetails(prev => {
      const updatedPhoneDetails = {
        ...prev,
        code: option,
        finalPhoneNo: `+${option.dialCode}${prev.phone}`
      };

      onChange(updatedPhoneDetails.finalPhoneNo);

      return updatedPhoneDetails;
    });

    setIsOpen(false);
  };

  const handlePhoneChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const phoneValue = event.target.value;
      setPhoneDetails(prev => ({
        ...prev,
        phone: phoneValue,
        finalPhoneNo: `+${phoneDetails.code.dialCode}${phoneValue}`
      }));
      onChange(`+${phoneDetails.code.dialCode}${phoneValue}`);
    },
    [phoneDetails, onChange]
  );

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  return (
    <div>
      <div className="relative " ref={dropdownRef}>
        <div className="p-3 absolute h-[48px] w-[80px] left-0 flex items-center pl-3 bg-#FFFFFF !border-#999999 !border-[0.5px]">
          <button
            type="button"
            id="dropdownButton"
            className="h-full !p-[0px] text-sm flex justify-center items-center bg-transparent text-#666666 focus:outline-none gap-[24px]"
            onClick={toggleDropdown}
          >
            +{phoneDetails?.code?.dialCode ?? ""}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 9 5"
              width="9"
              height="5"
            >
              <path d="M1 0.5L4.5 4L8 0.5" stroke="black" />
            </svg>
          </button>
        </div>

        <input
          type="hidden"
          name="countryCode"
          value={`${phoneDetails?.code?.dialCode ?? ""}`}
        />
        <div
          id="dropdownMenu"
          className={`w-[275px] h-40 overflow-hidden absolute left-0 top-[46px] ${
            isOpen ? "" : "hidden"
          } bg-white border border-slate-200 shadow-lg z-10`}
        >
          <ul
            id="dropdownOptions"
            className="w-[275px] h-40 px-[20px] pt-[15px] pb-[27px] overflow-y-auto scrollbar-thin scrollbar-thumb-rh-dark scrollbar-track-#FFFFFF"
          >
            {supportedCountries.map(ct => {
              const country = parseCountry(ct);
              return (
                <li
                  key={country?.dialCode}
                  className={`mt-[16px] mb-[16px] text-slate-600 hover:bg-slate-50 text-sm cursor-pointer ${
                    phoneDetails?.code?.dialCode === country.dialCode
                      ? "bg-slate-50"
                      : ""
                  }`}
                  onClick={() => handleCountryChange(country)}
                >
                  <div className="flex gap-[10px]">
                    <div className="w-[40px] h-[20px]">+{country.dialCode}</div>
                    <div className="w-auto h-[20px]">{country.name}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <TextField
          type="text"
          name="phoneNumber"
          className="!w-[232px] h-[48px] bg-transparent placeholder:text-slate-400 text-slate-700 text-sm border border-slate-200 pl-[20px] pr-[80px] py-[14px] transition duration-300 ease focus:outline-none shadow-sm focus:shadow ml-[79px]"
          placeholder="Phone number"
          value={phoneDetails?.phone ?? ""}
          onChange={handlePhoneChange}
        />
      </div>
    </div>
  );
};

export default memoize(StytchPhoneTextField);
